<template>
  <span v-if="hasAddon" :class="groupWrapperKls">
    <span :class="groupKls">
      <span
        v-if="$slots.addonBefore"
        :class="[ns.e('group-addon'), ns.em('group-addon', 'before')]"
      >
        <slot name="addonBefore" />
      </span>
      <div
        ref="wrapperRef"
        :class="wrapperKls"
        :style="wrapperStyle"
        @mousedown="handleRootMouseDown"
      >
        <span v-if="$slots.prefix || prefixIcon" :class="ns.e('prefix')">
          <slot name="prefix">
            <jd-icon v-if="prefixIcon"><component :is="prefixIcon" /></jd-icon>
          </slot>
        </span>

        <div ref="contentRef" :class="ns.e('content')">
          <div ref="listRef" :class="ns.e('input')">
            <div :class="listKls">
              <jd-tag
                v-for="(tag, index) in visibleTags"
                :key="`${tag}-${index}`"
                :class="ns.e('tag')"
                :type="resolveTagType(tag, index)"
                :size="tagSize"
                closable
                :disable-transitions="true"
                @close="handleTagClose(index)"
              >
                {{ tag }}
              </jd-tag>
            </div>
            <input
              ref="inputRef"
              :class="ns.e('inner')"
              :value="innerInputValue"
              :disabled="isDisabled"
              :placeholder="mergedTags.length ? '' : placeholder"
              @input="handleInputChange"
              @keydown="handleInputKeyDown"
              @paste="handleInputPaste"
              @focus="handleFocus"
              @blur="handleBlur"
              @compositionstart="handleCompositionStart"
              @compositionend="handleCompositionEnd"
            />
          </div>
        </div>

        <span
          v-if="showClearAll"
          :class="ns.e('clear-all')"
          @mousedown.prevent
          @click="handleClearAll"
        >
          <slot name="clearIcon"
            ><jd-icon><circle-close /></jd-icon
          ></slot>
        </span>

        <span
          v-if="showLength"
          ref="lengthRef"
          :class="lengthKls"
          @mousedown.prevent
          @click="handleLengthClick"
        >
          <span :class="ns.e('length-inner')">
            <span :class="ns.e('length-count')">{{ mergedTags.length }}</span>
            <span v-if="hasLimit" :class="ns.e('length-max-count')"
              >/{{ maxTags }}</span
            >
          </span>
        </span>

        <jd-popover
          v-if="showLength && showPopover"
          virtual-triggering
          :virtual-ref="popoverVirtualRef"
          :visible="popoverVisible"
          placement="bottom-end"
          :width="popoverWidth"
          :popper-class="ns.bm('popover', 'overlay')"
        >
          <div :class="ns.e('popover')">
            <div :class="ns.e('popover-tags')">
              <slot name="multiple-popover-content" :tags="mergedTags">
                <jd-tag
                  v-for="(tag, index) in mergedTags"
                  :key="`pop-${tag}-${index}`"
                  :type="resolveTagType(tag, index)"
                  closable
                  :disable-transitions="true"
                  @close="handleTagClose(index)"
                >
                  {{ tag }}
                </jd-tag>
              </slot>
            </div>
            <div
              v-if="showMultipleFooter || $slots['multiple-popover-footer']"
              :class="ns.e('popover-footer')"
            >
              <slot name="multiple-popover-footer">
                <jd-button type="text" @click="handleClearAll">
                  {{ multipleOperText }}
                </jd-button>
              </slot>
            </div>
          </div>
        </jd-popover>

        <span v-if="$slots.suffix || suffixIcon" :class="ns.e('suffix')">
          <slot name="suffix">
            <jd-icon v-if="suffixIcon"><component :is="suffixIcon" /></jd-icon>
          </slot>
        </span>
      </div>
      <span
        v-if="$slots.addonAfter"
        :class="[ns.e('group-addon'), ns.em('group-addon', 'after')]"
      >
        <slot name="addonAfter" />
      </span>
    </span>
  </span>

  <div
    v-else
    ref="wrapperRef"
    :class="wrapperKls"
    :style="wrapperStyle"
    @mousedown="handleRootMouseDown"
  >
    <span v-if="$slots.prefix || prefixIcon" :class="ns.e('prefix')">
      <slot name="prefix">
        <jd-icon v-if="prefixIcon"><component :is="prefixIcon" /></jd-icon>
      </slot>
    </span>

    <div ref="contentRef" :class="ns.e('content')">
      <div ref="listRef" :class="ns.e('input')">
        <div :class="listKls">
          <jd-tag
            v-for="(tag, index) in visibleTags"
            :key="`${tag}-${index}`"
            :class="ns.e('tag')"
            :type="resolveTagType(tag, index)"
            :size="tagSize"
            closable
            :disable-transitions="true"
            @close="handleTagClose(index)"
          >
            {{ tag }}
          </jd-tag>
        </div>
        <input
          ref="inputRef"
          :class="ns.e('inner')"
          :value="innerInputValue"
          :disabled="isDisabled"
          :placeholder="mergedTags.length ? '' : placeholder"
          @input="handleInputChange"
          @keydown="handleInputKeyDown"
          @paste="handleInputPaste"
          @focus="handleFocus"
          @blur="handleBlur"
          @compositionstart="handleCompositionStart"
          @compositionend="handleCompositionEnd"
        />
      </div>
    </div>

    <span
      v-if="showClearAll"
      :class="ns.e('clear-all')"
      @mousedown.prevent
      @click="handleClearAll"
    >
      <slot name="clearIcon"
        ><jd-icon><circle-close /></jd-icon
      ></slot>
    </span>

    <span
      v-if="showLength"
      ref="lengthRef"
      :class="lengthKls"
      @mousedown.prevent
      @click="handleLengthClick"
    >
      <span :class="ns.e('length-inner')">
        <span :class="ns.e('length-count')">{{ mergedTags.length }}</span>
        <span v-if="hasLimit" :class="ns.e('length-max-count')"
          >/{{ maxTags }}</span
        >
      </span>
    </span>

    <jd-popover
      v-if="showLength && showPopover"
      virtual-triggering
      :virtual-ref="popoverVirtualRef"
      :visible="popoverVisible"
      placement="bottom-end"
      :width="popoverWidth"
      :popper-class="ns.bm('popover', 'overlay')"
    >
      <div :class="ns.e('popover')">
        <div :class="ns.e('popover-tags')">
          <slot name="multiple-popover-content" :tags="mergedTags">
            <jd-tag
              v-for="(tag, index) in mergedTags"
              :key="`pop-${tag}-${index}`"
              :type="resolveTagType(tag, index)"
              closable
              :disable-transitions="true"
              @close="handleTagClose(index)"
            >
              {{ tag }}
            </jd-tag>
          </slot>
        </div>
        <div
          v-if="showMultipleFooter || $slots['multiple-popover-footer']"
          :class="ns.e('popover-footer')"
        >
          <slot name="multiple-popover-footer">
            <jd-button type="text" @click="handleClearAll">
              {{ multipleOperText }}
            </jd-button>
          </slot>
        </div>
      </div>
    </jd-popover>

    <span v-if="$slots.suffix || suffixIcon" :class="ns.e('suffix')">
      <slot name="suffix">
        <jd-icon v-if="suffixIcon"><component :is="suffixIcon" /></jd-icon>
      </slot>
    </span>
  </div>
</template>

<script lang="ts" setup>
import {
  computed,
  nextTick,
  onBeforeUnmount,
  onMounted,
  ref,
  useSlots,
  watch,
} from 'vue'
import { Close as CircleClose } from '@jd/jdesign-icons-vue'
import { useNamespace } from '@jdesign-vue/hooks'
import { UPDATE_MODEL_EVENT } from '@jdesign-vue/constants'
import { JdIcon } from '@jdesign-vue/components/icon'
import { JdTag } from '@jdesign-vue/components/tag'
import { JdPopover } from '@jdesign-vue/components/popover'
import { JdButton } from '@jdesign-vue/components/button'
import {
  useFormDisabled,
  useFormItem,
  useFormSize,
} from '@jdesign-vue/components/form'
import { addUnit } from '@jdesign-vue/utils'
import { inputTagEmits, inputTagProps } from './input-tag'
import type { InputTagType } from './input-tag'

defineOptions({ name: 'JdInputTag' })

const props = defineProps(inputTagProps)
const emit = defineEmits(inputTagEmits)

const slots = useSlots()

const ns = useNamespace('input-tag')
const { formItem } = useFormItem()
const inputSize = useFormSize()
const isDisabled = useFormDisabled(computed(() => props.disabled))

const inputRef = ref<HTMLInputElement>()
const contentRef = ref<HTMLElement>()
const listRef = ref<HTMLElement>()
const wrapperRef = ref<HTMLElement>()
const lengthRef = ref<HTMLElement>()

// popover 受控显示
const popoverVisible = ref(false)

// 合成虚拟锚点：水平对齐计数徽标（箭头指向徽标），垂直对齐输入框底部（弹窗从输入框底部弹出）
const popoverVirtualRef = {
  getBoundingClientRect() {
    const wrapperRect = wrapperRef.value?.getBoundingClientRect()
    if (!wrapperRect) return new DOMRect()
    const lengthRect = lengthRef.value?.getBoundingClientRect()
    const left = lengthRect ? lengthRect.left : wrapperRect.left
    const right = lengthRect ? lengthRect.right : wrapperRect.right
    return DOMRect.fromRect({
      x: left,
      y: wrapperRect.top,
      width: right - left,
      height: wrapperRect.height,
    })
  },
}

// 受控/非受控合并
const innerTags = ref<string[]>([...(props.modelValue || [])].map(String))
const innerInputValue = ref('')
const focused = ref(false)
const composing = ref(false)
const overflowed = ref(false)
const pasting = ref(false)

const isControlled = computed(() => Array.isArray(props.modelValue))
const mergedTags = computed(() =>
  isControlled.value ? (props.modelValue || []).map(String) : innerTags.value
)

const hasLimit = computed(
  () => typeof props.maxTags === 'number' && props.maxTags >= 0
)
const reachMaxLimit = computed(
  () => hasLimit.value && mergedTags.value.length >= (props.maxTags as number)
)
const exceedMaxCount = computed(
  () =>
    props.enabledExceed &&
    hasLimit.value &&
    mergedTags.value.length > (props.maxTags as number)
)

const showLength = computed(() => props.showCount || props.showPopover)
const showClearAll = computed(
  () => props.allowClear && !isDisabled.value && mergedTags.value.length > 0
)
const visibleTags = computed(() => mergedTags.value)

const tagSize = computed(() =>
  inputSize.value === 'large'
    ? 'large'
    : inputSize.value === 'small'
    ? 'small'
    : ''
)
const popoverWidth = ref<string | number | undefined>(undefined)

const wrapperStyle = computed(() => {
  const w = props.width
  return w != null ? { width: addUnit(w) } : undefined
})

const hasAddon = computed(() => !!(slots.addonBefore || slots.addonAfter))
const groupWrapperKls = computed(() => [
  ns.b('group-wrapper'),
  ns.is('disabled', isDisabled.value),
  props.status ? ns.bm('group-wrapper', `status-${props.status}`) : '',
])
const groupKls = computed(() => [
  ns.e('wrapper'),
  ns.e('group'),
  props.addonBeforeBg ? ns.is('before-addon-bg') : '',
  props.addonAfterBg ? ns.is('after-addon-bg') : '',
])

const wrapperKls = computed(() => [
  ns.b(),
  inputSize.value ? ns.m(inputSize.value) : '',
  ns.is('focused', focused.value),
  ns.is('disabled', isDisabled.value),
  ns.is('exceed', !!exceedMaxCount.value),
  ns.is('maxed', reachMaxLimit.value && !props.enabledExceed),
  ns.is('borderless', !props.bordered),
  ns.is('round', props.round),
  ns.is('has-tags', mergedTags.value.length > 0),
  props.status ? ns.is(props.status) : '',
])

const listKls = computed(() => [
  ns.e('list'),
  ns.is('overflowed', overflowed.value),
])

// 无标签时计数不可点击（点击不弹窗），避免弹出空浮层
const canOpenPopover = computed(
  () => props.showPopover && mergedTags.value.length > 0
)

const lengthKls = computed(() => [
  ns.e('length'),
  ns.is('exceed', !!exceedMaxCount.value),
  canOpenPopover.value ? ns.is('clickable') : '',
])

// ---- splitter helpers ----
function escapeForCharClass(str: string) {
  return str.replace(/[\]\-^[]/g, '\\$&')
}
function getSplitterRegExp(): RegExp {
  const { splitter } = props
  if (splitter instanceof RegExp) {
    return new RegExp(`${splitter.source}|,|[\\s\\u00A0\\u3000]+`, 'g')
  }
  if (typeof splitter === 'string' && splitter.length) {
    return new RegExp(`[${escapeForCharClass(splitter)}\\s\\u00A0\\u3000]`, 'g')
  }
  return /[,\s\u00A0\u3000]/g
}
function splitAllInputToTags(input: string): string[] {
  if (!input) return []
  return input
    .split(getSplitterRegExp())
    .map((s) => s.trim())
    .filter(Boolean)
}
function splitInputBySplitter(input: string): { tags: string[]; rest: string } {
  const re = getSplitterRegExp()
  if (!re.test(input)) return { tags: [], rest: input }
  const parts = input.split(getSplitterRegExp())
  const rest = parts.pop() ?? ''
  const tags = parts.map((s) => s.trim()).filter(Boolean)
  return { tags, rest }
}

// ---- tag mutations ----
function updateTags(next: string[]) {
  if (!isControlled.value) {
    innerTags.value = next
  }
  emit(UPDATE_MODEL_EVENT, next)
  emit('multiple-change', next)
  emit('change', next)
  formItem?.validate?.('change').catch(() => undefined)
}

function appendTags(tagsToAppend: string[], nextInputValue: string) {
  if (!tagsToAppend.length) {
    triggerInputValue(nextInputValue)
    return
  }
  const current = mergedTags.value
  const allowAdd =
    !isDisabled.value &&
    (props.enabledExceed ||
      !hasLimit.value ||
      current.length < (props.maxTags as number))

  if (!allowAdd) {
    triggerInputValue(nextInputValue)
    return
  }

  // 先截断，再按最终标签文本去重
  let candidates = tagsToAppend
  if (typeof props.tagMaxLength === 'number') {
    candidates = candidates.map((t) => t.slice(0, props.tagMaxLength as number))
  }
  if (props.unique) {
    const exist = new Set(current)
    candidates = candidates.filter((t) => {
      if (exist.has(t)) return false
      exist.add(t)
      return true
    })
  }
  // 未开启 exceed 且有上限时截断可加入数量
  if (!props.enabledExceed && hasLimit.value) {
    const availableCount = (props.maxTags as number) - current.length
    candidates = candidates.slice(0, Math.max(0, availableCount))
  }
  if (!candidates.length) {
    triggerInputValue(nextInputValue)
    return
  }
  const nextTags = [...current, ...candidates]
  updateTags(nextTags)
  candidates.forEach((t) => emit('tag-add', t, nextTags))
  triggerInputValue(nextInputValue)

  // 达上限失焦
  if (
    !props.enabledExceed &&
    hasLimit.value &&
    nextTags.length >= (props.maxTags as number)
  ) {
    focused.value = false
    inputRef.value?.blur()
  }
}

function triggerInputValue(next: string) {
  innerInputValue.value = next
  emit('input-change', next)
}

// ---- resolve tag type (React 'danger' -> Vue 'error') ----
function resolveTagType(tag: string, index: number): InputTagType {
  const { tagType } = props
  let type: any
  if (typeof tagType === 'function') {
    type = tagType(tag, index, mergedTags.value)
  } else {
    type = tagType
  }
  if (type === 'danger') type = 'error'
  return (type ?? 'gray') as InputTagType
}

// ---- events ----
function handleInputChange(evt: Event) {
  const value = (evt.target as HTMLInputElement).value
  if (composing.value || pasting.value) {
    triggerInputValue(value)
    return
  }
  const { tags, rest } = splitInputBySplitter(value)
  appendTags(tags, rest)
}

function handleInputKeyDown(evt: KeyboardEvent) {
  if (evt.key === 'Enter') {
    emit('press-enter', evt)
  }
  if (!composing.value && (evt.key === 'Enter' || evt.key === ' ')) {
    evt.preventDefault()
    const value = (evt.target as HTMLInputElement).value
    const tags = splitAllInputToTags(value)
    appendTags(tags, '')
    return
  }
  if (
    evt.key === 'Backspace' &&
    !innerInputValue.value &&
    mergedTags.value.length > 0
  ) {
    const next = mergedTags.value.slice(0, -1)
    const removed = mergedTags.value[mergedTags.value.length - 1]
    updateTags(next)
    emit('tag-remove', removed, next)
  }
}

function handleInputPaste() {
  pasting.value = true
  setTimeout(() => {
    const value = inputRef.value?.value ?? ''
    const tags = splitAllInputToTags(value)
    appendTags(tags, '')
    pasting.value = false
  }, 0)
}

function handleCompositionStart() {
  composing.value = true
}
function handleCompositionEnd(evt: CompositionEvent) {
  composing.value = false
  const value = (evt.target as HTMLInputElement).value
  const { tags, rest } = splitInputBySplitter(value)
  appendTags(tags, rest)
}

function handleTagClose(index: number) {
  if (isDisabled.value) return
  const removed = mergedTags.value[index]
  const next = mergedTags.value.filter((_, i) => i !== index)
  updateTags(next)
  emit('tag-remove', removed, next)
}

function handleClearAll() {
  if (isDisabled.value) return
  const removedTags = [...mergedTags.value]
  updateTags([])
  removedTags.forEach((t) => emit('tag-remove', t, []))
  // Bug1: 清空后关闭 popover
  popoverVisible.value = false
}

function handleFocus(evt: FocusEvent) {
  focused.value = true
  emit('focus', evt)
  // 聚焦后将列表横向滚动到末尾，露出最后一个 tag 和输入框，避免被裁切
  nextTick(() => {
    const el = listRef.value
    if (el) el.scrollLeft = el.scrollWidth
  })
}
function handleBlur(evt: FocusEvent) {
  focused.value = false
  // 失焦时把草稿转成 tag
  if (innerInputValue.value) {
    const tags = splitAllInputToTags(innerInputValue.value)
    appendTags(tags, '')
  }
  emit('blur', evt)
  formItem?.validate?.('blur').catch(() => undefined)
}

function handleRootMouseDown(evt: MouseEvent) {
  const target = evt.target as HTMLElement
  if (
    target !== inputRef.value &&
    !target.closest(`.${ns.e('tag')}`) &&
    !target.closest(`.${ns.e('clear-all')}`) &&
    !target.closest(`.${ns.e('length')}`)
  ) {
    evt.preventDefault()
    inputRef.value?.focus()
  }
}

function handleLengthClick() {
  // 数量为 0 时点击计数不弹窗
  if (!canOpenPopover.value) {
    popoverVisible.value = false
    return
  }
  popoverVisible.value = !popoverVisible.value
}

// 点击外部关闭 popover（受控模式需自行实现）
function handleDocumentMouseDown(evt: MouseEvent) {
  const target = evt.target as HTMLElement
  if (!target) return
  // 点击输入框内部不关闭
  if (wrapperRef.value && wrapperRef.value.contains(target)) return
  // 点击 popover 浮层内部不关闭
  if (target.closest(`.${ns.bm('popover', 'overlay')}`)) return
  popoverVisible.value = false
}

// ---- overflow detection ----
let resizeObserver: ResizeObserver | undefined
function measureOverflow() {
  const el = listRef.value
  if (!el) return
  overflowed.value = el.scrollWidth > el.clientWidth + 2
}
onMounted(() => {
  nextTick(measureOverflow)
  if (typeof ResizeObserver !== 'undefined' && listRef.value) {
    resizeObserver = new ResizeObserver(() => measureOverflow())
    resizeObserver.observe(listRef.value)
  }
})
onBeforeUnmount(() => {
  resizeObserver?.disconnect()
  document.removeEventListener('mousedown', handleDocumentMouseDown, true)
})

// 标签被删空（含浮层内逐个关闭）时自动收起浮层
watch(canOpenPopover, (can) => {
  if (!can) popoverVisible.value = false
})

// popover 显示时监听外部点击，并同步输入框宽度（保证箭头居中、弹窗与输入框对齐）
watch(popoverVisible, (visible) => {
  if (visible) {
    if (wrapperRef.value) {
      popoverWidth.value = wrapperRef.value.offsetWidth
    }
    document.addEventListener('mousedown', handleDocumentMouseDown, true)
  } else {
    document.removeEventListener('mousedown', handleDocumentMouseDown, true)
  }
})

// 受控值变化清空草稿
watch(
  () => (isControlled.value ? (props.modelValue || []).join('\u0001') : ''),
  () => {
    if (isControlled.value) {
      innerInputValue.value = ''
    }
    nextTick(measureOverflow)
  }
)

defineExpose({
  focus: () => inputRef.value?.focus(),
  blur: () => inputRef.value?.blur(),
})
</script>
